<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import CollapsibleSection from './CollapsibleSection.vue';
import ProductGalleryPanel from './ProductGalleryPanel.vue';
import './epp-shared.css';

export type VariantLang = 'fr' | 'en' | 'nl';
export type VariantStatus = 'active' | 'inactive' | 'out-of-stock';

export interface Variant {
  _key: string;
  id?: string;
  labelFr: string;
  labelEn: string;
  labelNl: string;
  labelLang: VariantLang;
  price: string;
  priceUnit: string;
  packageQty: string;
  packageUnit: string;
  isDefault: boolean;
  status: VariantStatus;
  translating: boolean;
}

export interface SelectOption {
  value: string;
  label: string;
}

defineProps<{
  productId: string | null;
  variants: Variant[];
  priceUnitOptions: SelectOption[];
  packageUnitOptions: SelectOption[];
  addVariant: () => void;
  removeVariant: (index: number) => void;
  setDefaultVariant: (index: number) => void;
  onVariantLabelBlur: (index: number, lang: VariantLang) => void;
  showVariantPriceError: (index: number) => boolean;
}>();

const { t } = useI18n();
</script>

<template>
  <CollapsibleSection
    section-key="variants"
    icon="pi-list"
    :title="t('product.variantsOptional')"
    :subtitle="t('product.variantsSubtitle')"
    :default-open="true"
  >
    <div class="epp__variants-tab-hd">
      <button class="epp__add-btn" @click="addVariant">
        <i class="pi pi-plus" /> {{ t('product.add') }}
      </button>
    </div>

    <div v-if="variants.length" class="epp__variants">
      <div v-for="(v, i) in variants" :key="v._key" class="epp__variant-card">
        <div class="epp__variant-header">
          <span class="epp__variant-num">{{ i + 1 }}</span>
          <span v-if="v.id" class="epp__existing-badge">{{
            t('product.existingVariantBadge')
          }}</span>
          <div class="epp__select-wrap" style="max-width: 9rem">
            <select v-model="v.labelLang" class="epp__select epp__select--sm">
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
            </select>
            <i class="pi pi-chevron-down epp__select-icon" />
          </div>
          <span
            v-if="v.translating"
            class="epp__translating-dot"
            style="margin-left: 0.25rem"
          />
          <button
            class="epp__icon-btn epp__icon-btn--danger"
            style="margin-left: auto"
            @click="removeVariant(i)"
          >
            <i class="pi pi-trash" />
          </button>
        </div>
        <div class="epp__field-row epp__field-row--3">
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="v.labelLang === 'fr' ? 'epp__lang-badge--active' : ''"
                >FR</span
              ></label
            >
            <input
              v-model="v.labelFr"
              class="epp__input"
              :class="v.labelLang === 'fr' ? 'epp__input--primary' : ''"
              type="text"
              :placeholder="t('product.variantFrPlaceholder')"
              @blur="onVariantLabelBlur(i, 'fr')"
            />
          </div>
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="v.labelLang === 'en' ? 'epp__lang-badge--active' : ''"
                >EN</span
              ></label
            >
            <input
              v-model="v.labelEn"
              class="epp__input"
              :class="v.labelLang === 'en' ? 'epp__input--primary' : ''"
              type="text"
              placeholder="ex: Large"
              @blur="onVariantLabelBlur(i, 'en')"
            />
          </div>
          <div class="epp__field">
            <label class="epp__label"
              ><span
                class="epp__lang-badge"
                :class="v.labelLang === 'nl' ? 'epp__lang-badge--active' : ''"
                >NL</span
              ></label
            >
            <input
              v-model="v.labelNl"
              class="epp__input"
              :class="v.labelLang === 'nl' ? 'epp__input--primary' : ''"
              type="text"
              placeholder="ex: Groot"
              @blur="onVariantLabelBlur(i, 'nl')"
            />
          </div>
        </div>
        <div class="epp__field-row epp__field-row--3">
          <div class="epp__field">
            <label class="epp__label">{{ t('product.priceEur') }}</label>
            <div class="epp__price-wrap">
              <input
                v-model="v.price"
                class="epp__input epp__input--price"
                :class="{ 'epp__input--error': showVariantPriceError(i) }"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
              />
              <span class="epp__price-unit">€</span>
            </div>
            <p v-if="showVariantPriceError(i)" class="epp__field-error">
              {{ t('product.requiredField') }}
            </p>
          </div>
          <div class="epp__field">
            <label class="epp__label">{{ t('product.priceUnit') }}</label>
            <div class="epp__select-wrap">
              <select v-model="v.priceUnit" class="epp__select">
                <option
                  v-for="o in priceUnitOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.label }}
                </option>
              </select>
              <i class="pi pi-chevron-down epp__select-icon" />
            </div>
          </div>
          <div class="epp__field">
            <label class="epp__label">{{ t('product.statusLabel') }}</label>
            <div class="epp__select-wrap">
              <select v-model="v.status" class="epp__select">
                <option value="active">{{ t('product.statusActive') }}</option>
                <option value="inactive">
                  {{ t('product.statusInactive') }}
                </option>
                <option value="out-of-stock">
                  {{ t('product.statusOutOfStock') }}
                </option>
              </select>
              <i class="pi pi-chevron-down epp__select-icon" />
            </div>
          </div>
        </div>
        <div class="epp__field-row epp__field-row--3">
          <div class="epp__field">
            <label class="epp__label">{{ t('product.soldByLabel') }}</label>
            <div class="epp__select-wrap">
              <select v-model="v.packageUnit" class="epp__select">
                <option
                  v-for="o in packageUnitOptions"
                  :key="o.value"
                  :value="o.value"
                >
                  {{ o.label }}
                </option>
              </select>
              <i class="pi pi-chevron-down epp__select-icon" />
            </div>
          </div>
          <div class="epp__field">
            <label class="epp__label">{{ t('product.qtyPerFormat') }}</label>
            <input
              v-model="v.packageQty"
              class="epp__input"
              type="number"
              min="1"
              placeholder="ex: 6"
            />
          </div>
          <div class="epp__field epp__field--center">
            <label class="epp__label">{{ t('product.isDefault') }}</label>
            <label class="epp__toggle">
              <input
                type="checkbox"
                v-model="v.isDefault"
                class="epp__toggle-input"
                @change="setDefaultVariant(i)"
              />
              <span class="epp__toggle-track"
                ><span class="epp__toggle-thumb"
              /></span>
            </label>
          </div>
        </div>

        <!-- Galerie propre à cette déclinaison — seulement une fois la
         * déclinaison créée côté serveur (v.id), et vide par défaut : elle
         * hérite alors des images du produit. -->
        <div v-if="productId && v.id" class="epp__variant-gallery">
          <div class="epp__variant-gallery-hd">
            <i class="pi pi-images" />
            <span>Galerie de cette déclinaison</span>
          </div>
          <ProductGalleryPanel :product-id="productId" :variant-id="v.id" />
        </div>
      </div>
    </div>
    <div v-else class="epp__empty">
      <i class="pi pi-list epp__empty-icon" />
      <p>{{ t('product.noVariantsHint') }}</p>
    </div>
  </CollapsibleSection>
</template>

<style scoped>
.epp__variants-tab-hd {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.875rem;
}

.epp__variants {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.epp__variant-card {
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
  padding: 1.125rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.epp__variant-gallery {
  padding-top: 0.875rem;
  border-top: 1px dashed var(--nxt-outline-variant);
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.epp__variant-gallery-hd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.epp__variant-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.epp__variant-num {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: var(--nxt-surface-mid);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 800;
  color: var(--nxt-on-surface-muted);
  flex-shrink: 0;
}

.epp__toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.epp__toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.epp__toggle-track {
  width: 2.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  background: var(--nxt-outline-variant);
  transition: background 0.2s;
  position: relative;
}
.epp__toggle-input:checked + .epp__toggle-track {
  background: var(--nxt-primary);
}
.epp__toggle-thumb {
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.epp__toggle-input:checked + .epp__toggle-track .epp__toggle-thumb {
  transform: translateX(1rem);
}
</style>

<style>
[data-theme='dark'] .epp__variant-card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
</style>
